import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import { IdentifierType, BulkCreateSignupSingleShiftResponse } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useBulkCreateSignup } from '../../hooks/signup/useBulkCreateSignup';
import { publishKatalMetric } from '../katalAnalytics';

const MAX_BULK_ADD_ENTRIES = 1000;
const MAX_ENTRY_LENGTH = 100;
const BULLET = '•';

interface BulkAddSignupsContentProps {
  eventId: string;
  shiftId: string;
  bulkSignupsList: string;
  onBulkSignupsListChange: (value: string) => void;
  identifierTypeValue: IdentifierType;
  onIdentifierTypeChange: (value: IdentifierType) => void;
  onValidationSuccess: (
    data: BulkCreateSignupSingleShiftResponse,
    identifierType: IdentifierType
  ) => void;
  onCancel: () => void;
  metricKeys: {
    validateSignupsMetricKey: string;
    validationSuccessCountMetricKey: string;
    validationErrorsCountMetricKey: string;
    validationSkippedCountMetricKey: string;
  };
}

export function BulkAddSignupsContent({
  eventId,
  shiftId,
  bulkSignupsList,
  onBulkSignupsListChange,
  identifierTypeValue,
  onIdentifierTypeChange,
  onValidationSuccess,
  onCancel,
  metricKeys,
}: BulkAddSignupsContentProps) {
  const { t } = useTranslation('signup');
  const bulkCreateSignup = useBulkCreateSignup(eventId);

  const [errorText, setErrorText] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const identifierOptions: SelectProps.Options = [
    {
      label: t('bulkSignupModal.bulkAdd.identifierOptions.emailOrAlias'),
      value: IdentifierType.ALIAS,
    },
    {
      label: t('bulkSignupModal.bulkAdd.identifierOptions.employee'),
      value: IdentifierType.EMPLOYEE_ID,
    },
    {
      label: t('bulkSignupModal.bulkAdd.identifierOptions.badge'),
      value: IdentifierType.BADGE_ID,
      disabled: true,
      disabledReason: t('bulkSignupModal.bulkAdd.identifierOptions.badgeDisabledReason'),
    },
  ];

  const handleValidate = async () => {
    if (!bulkSignupsList.trim()) {
      setErrorText(t('bulkSignupModal.bulkAdd.errors.empty'));
      return;
    }

    const entries = bulkSignupsList
      .split(/[\t,\n]+/)
      .map((entry) => {
        const trimmed = entry.trim();
        const atIndex = trimmed.indexOf('@');
        return atIndex !== -1 ? trimmed.substring(0, atIndex) : trimmed;
      })
      .filter((entry) => entry.length > 0);

    if (entries.length === 0) {
      setErrorText(t('bulkSignupModal.bulkAdd.errors.empty'));
      return;
    }

    if (entries.length > MAX_BULK_ADD_ENTRIES) {
      setErrorText(t('bulkSignupModal.bulkAdd.errors.tooMany'));
      return;
    }

    if (entries.some((entry) => entry.length >= MAX_ENTRY_LENGTH)) {
      setErrorText(t('bulkSignupModal.bulkAdd.errors.entryTooLong'));
      return;
    }

    setIsValidating(true);
    try {
      const response = await bulkCreateSignup.mutateAsync({
        validationOnly: true,
        shiftSignupUsersMap: {
          [shiftId]: entries.map((entry) => ({
            identifierType: identifierTypeValue,
            identifierValue: entry,
          })),
        },
      });

      const shiftResponse = response.shiftSignupResponseMap[shiftId];
      publishKatalMetric(metricKeys.validateSignupsMetricKey, '');
      publishKatalMetric(
        metricKeys.validationSuccessCountMetricKey,
        shiftResponse.validSignupUsers.length.toString()
      );
      publishKatalMetric(
        metricKeys.validationErrorsCountMetricKey,
        shiftResponse.invalidSignupUsers.length.toString()
      );
      publishKatalMetric(
        metricKeys.validationSkippedCountMetricKey,
        shiftResponse.alreadySignedUpUsers.length.toString()
      );
      onValidationSuccess(shiftResponse, identifierTypeValue);
    } catch {
      setErrorText(t('bulkSignupModal.bulkAdd.errors.failedValidation'));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <div className="bulk-upload-modal-scrollable">
        <Container header={<Header variant="h2">{t('bulkSignupModal.bulkAdd.header')}</Header>}>
          <SpaceBetween size="l">
            <FormField label={t('bulkSignupModal.bulkAdd.identifierLabel')}>
              <Select
                options={identifierOptions}
                selectedOption={
                  identifierOptions.find((o) => o.value === identifierTypeValue) ||
                  identifierOptions[0]
                }
                onChange={({ detail }) =>
                  onIdentifierTypeChange(detail.selectedOption.value as IdentifierType)
                }
              />
            </FormField>
            <FormField
              label={t('bulkSignupModal.bulkAdd.listLabel')}
              errorText={errorText}
              constraintText={
                <>
                  {BULLET} {t('bulkSignupModal.bulkAdd.constraints.duplicates')}
                  <br />
                  {BULLET} {t('bulkSignupModal.bulkAdd.constraints.alreadySignedUp')}
                  <br />
                  {BULLET} {t('bulkSignupModal.bulkAdd.constraints.maximum')}
                </>
              }
              stretch
            >
              <Textarea
                placeholder={t('bulkSignupModal.bulkAdd.listPlaceholder')}
                rows={8}
                value={bulkSignupsList}
                onChange={({ detail }) => {
                  onBulkSignupsListChange(detail.value);
                  if (errorText) setErrorText('');
                }}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </div>
      <Box padding={{ top: 'l' }}>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              {t('bulkSignupModal.buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={handleValidate} loading={isValidating}>
              {t('bulkSignupModal.buttons.validate')}
            </Button>
          </SpaceBetween>
        </Box>
      </Box>
    </>
  );
}
